import React from 'react';
import {Typography} from '../../typography';
import {OutlinedCardWrapper, OutlinedCardWrapperProps} from './OutlinedCard.styles';
import {Eyebrow, EyebrowProps} from '../../eyebrow';
import {UeLink} from '../../../wraps';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import {VerticalSpacer} from '../../spacer';
import {useUnrealUIContext} from '../../../../shared/context/componentContext';
import {useIsLightTheme, useIsTwinmotionTheme} from 'epic-ue-theme/src/shared/theme.hooks';
import {UeTheme} from 'epic-ue-theme/src/ueTheme.types';
import useTheme from '@material-ui/core/styles/useTheme';

export interface OutlinedCardProps {
    eyebrow?: EyebrowProps;
    title?: string;
    titleAs?: 'h2' | 'h3';
    href?: string;
    description?: string;
    className?: string;
    children?: string | React.ReactNode;
    accentColor?: AccentColorOptions;
    centered?: boolean;
    groupedHeight?: boolean;
    topSpacing?: boolean;
    topVerticalSpacing?: number;
    fillParentSize?: boolean;
    paragraphCaption?: boolean;
    titleVariant?: 'h2' | 'h3';
    titleBig?: boolean;
    carbonAluminiumCards?: boolean;
    largeCards?: boolean;
    showBorder?: boolean;
}

export const OutlinedCard = (props: OutlinedCardProps): JSX.Element => {
    const {
        eyebrow,
        title,
        href,
        description,
        className,
        children,
        centered,
        groupedHeight,
        topSpacing = true,
        topVerticalSpacing = 9,
        fillParentSize = false,
        paragraphCaption = true,
        titleVariant = 'h3',
        titleBig = true,
        titleAs,
        largeCards = false,
        carbonAluminiumCards = false,
        showBorder
    } = props;
    const {accentColor = 'sky'} = props;

    const {outlinedCard} = useUnrealUIContext();
    const outlinedCardProps: OutlinedCardWrapperProps = {};
    const theme: UeTheme = useTheme();
    const isLight = useIsLightTheme();
    const isTwinmotion = useIsTwinmotionTheme();

    if (carbonAluminiumCards) {
        outlinedCardProps.$cardBackgroundColor =
            isLight && !largeCards ? theme.palette.neutral.carbon : theme.palette.neutral.aluminium;
        outlinedCardProps.$textColor = isLight
            ? theme.palette.common.white
            : theme.palette.common.black;
        outlinedCardProps.$hoverCardBackgroundColor = isLight ? 'white' : 'black';
        outlinedCardProps.$hoverArrowColor = isLight
            ? theme.palette.accent.dark.orchid
            : theme.palette.accent.bright.orchid;
        outlinedCardProps.$hoverTextColor = isLight ? 'black' : 'white';
    }

    if (showBorder && outlinedCard?.noBorder) {
        outlinedCard.noBorder = false;
    }

    const handheld = useBreakpointDown('sm');
    const centeredAttr = (centered || '').toString();
    const groupedAttr = (groupedHeight || '').toString();
    const handheldAttr = (handheld || '').toString();
    const rootClasses = [
        'card',
        'ue-outlined-card',
        accentColor,
        className,
        href ? 'clickable' : '',
        fillParentSize ? 'fill-parent' : '',
        outlinedCard?.noBorder ? 'no-border' : ''
    ].join(' ');

    return (
        <OutlinedCardWrapper
            {...outlinedCardProps}
            centered={centeredAttr}
            grouped={groupedAttr}
            $handheld={handheldAttr}
            $cardNoBorder={outlinedCard?.noBorder}
            classes={{root: rootClasses}}
            $isTwinmotion={isTwinmotion}
            id="outlinedCard">
            <UeLink href={href}>
                <React.Fragment>
                    {eyebrow && (eyebrow.text || eyebrow.children) && (
                        <Eyebrow
                            {...eyebrow}
                            hasRightArrow={outlinedCard?.arrows === 'arrow-right-alt'}
                        />
                    )}
                    {topSpacing && <VerticalSpacer variant="custom" spacing={topVerticalSpacing} />}
                    <div className="card-content">
                        {title && (
                            <Typography
                                variant={titleVariant}
                                big={!handheld && titleBig}
                                as={titleAs}>
                                {title}
                            </Typography>
                        )}
                        {description && (
                            <>
                                <VerticalSpacer variant="paragraph" />
                                <Typography variant="p" caption={paragraphCaption}>
                                    {description}
                                </Typography>
                            </>
                        )}
                    </div>
                    {children && children}
                </React.Fragment>
            </UeLink>
        </OutlinedCardWrapper>
    );
};
